import { initSwiper } from './swiper';

const LOADED_FLAG = 'recsLoaded';
const SLIDE_SELECTOR = '.swiper-slide';

// The upsell can exist twice at once (cart page + minicart drawer), and AC's
// load() injects into the first selector match only — so each AC container
// gets a unique attribute value to target.
const getAcsTargetValue = (containerEl) =>
  containerEl.closest('[data-minicart]') ? 'minicart' : 'page';

// AC ignores request args, so the in-cart/excluded-products filter runs here
// instead: drop rendered slides whose product handle is in the exclude list
// (slides key on data-product-card-atc="{handle}"). The S&D fallback filters
// server-side, so its containers carry no handle list and this no-ops.
const removeExcludedSlides = (containerEl) => {
  const handles = (containerEl.dataset.excludeProductHandles || '').split(',').filter(Boolean);
  if (!handles.length) return;

  const slideEls = containerEl.querySelectorAll(SLIDE_SELECTOR);
  slideEls.forEach((slideEl) => {
    const handle = slideEl.querySelector('[data-product-card-atc]')?.dataset.productCardAtc;
    if (handle && handles.includes(handle)) slideEl.remove();
  });
};

// The card ATC buttons open the quick-add drawer through the document-delegated
// [data-quick-add-trigger] handler in drawer.js, so no per-injection wiring is
// needed here beyond the swiper.
const finishInjection = (containerEl) => {
  if (!containerEl.querySelector(SLIDE_SELECTOR)) {
    containerEl.innerHTML = '';
    return;
  }

  const isMinicart = !!containerEl.closest('[data-minicart]');
  const sliderEl = containerEl.querySelector('[data-swiper]');
  if (!sliderEl) return;

  if (isMinicart) sliderEl.setAttribute('data-minicart-upsell-swiper', '');
  if (!window.theme.swiperObjects) window.theme.swiperObjects = [];
  const swiper = initSwiper(sliderEl);
  if (swiper) window.theme.swiperObjects.push(swiper);
};

const loadFallbackRecs = async (containerEl) => {
  const url = containerEl.dataset.url;
  if (!url) return;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionEl = doc.querySelector('.shopify-section');
    if (!sectionEl || !sectionEl.innerHTML.trim()) return;

    containerEl.innerHTML = sectionEl.innerHTML;
    finishInjection(containerEl);
  } catch (err) {
    console.error('Error fetching cart recommendations:', err);
  }
};

const loadAcsRecs = (containerEl) => {
  // No GrapheneHC library (blocked, CDN failure) — fall back to Search &
  // Discovery, matching complete-look.js and recommended-products.js.
  if (typeof window.graphenehc?.load !== 'function') {
    loadFallbackRecs(containerEl);
    return;
  }

  const targetValue = getAcsTargetValue(containerEl);
  containerEl.setAttribute('data-acs-basket-recs', targetValue);

  // The basket_recs element requires a context product ("product_id was not
  // supplied" warning otherwise), and the cart/minicart URL the library sends
  // as x_page_url is not a product page — so the product page_context must be
  // passed explicitly here. The Liquid picks the first cart item NOT in the
  // excluded-products theme setting (add-ons like Loxa insurance are
  // unresolvable by the strategy — using one as context makes the element fail
  // server-side and success never fires) and passes its id and URL.
  const contextProductId = containerEl.dataset.contextProductId;
  const contextProductUrl = containerEl.dataset.contextProductUrl;
  if (!contextProductId || !contextProductUrl) return;

  window.graphenehc.load({
    elements: [{ id: 'basket_recs', target: `[data-acs-basket-recs="${targetValue}"]` }],
    // NOTE: request `args` never reach element template renders (verified
    // 2026-07-27) — exclusions are applied client-side in finishInjection.
    // eslint-disable-next-line camelcase -- GrapheneHC API requires snake_case keys.
    page_context: {
      // eslint-disable-next-line camelcase -- GrapheneHC API requires snake_case keys.
      currency_id: window.Shopify.currency.active,
      // eslint-disable-next-line camelcase -- GrapheneHC API requires snake_case keys.
      product_id: contextProductId,
      // eslint-disable-next-line camelcase -- GrapheneHC API requires snake_case keys.
      page_url: window.location.origin + contextProductUrl,
    },
    success: () => {
      // AC ignores request args, so the in-cart/excluded-products filter is
      // applied to the rendered slides here (the S&D fallback filters
      // server-side and needs no removal pass). If the element returned
      // nothing — or nothing eligible remains — fall back to S&D.
      removeExcludedSlides(containerEl);
      if (!containerEl.querySelector(SLIDE_SELECTOR)) {
        containerEl.innerHTML = '';
        loadFallbackRecs(containerEl);
        return;
      }
      finishInjection(containerEl);
    },
  });
};


const initCartRecs = () => {
  const acsContainerEls = document.querySelectorAll('[data-acs-basket-recs]');
  acsContainerEls.forEach((containerEl) => {
    if (containerEl.dataset[LOADED_FLAG] === 'true') return;
    containerEl.dataset[LOADED_FLAG] = 'true';
    loadAcsRecs(containerEl);
  });

  const fallbackContainerEls = document.querySelectorAll('[data-cart-recs]');
  fallbackContainerEls.forEach((containerEl) => {
    if (containerEl.dataset[LOADED_FLAG] === 'true') return;
    containerEl.dataset[LOADED_FLAG] = 'true';
    loadFallbackRecs(containerEl);
  });
};

export default initCartRecs;
